import * as React from 'react';
import { observer } from 'mobx-react';
import { classNames } from 'uitk-react-utils';
import { UitkText } from 'uitk-react-text';
import { ListDisplayFilterGroup, CardDisplayFilterGroup, SkeletonFilterGroup } from '.';
import { FlightsFilterOptionType, LoadedFilterPresentationType } from 'types/search/sort-filter';
import { useStores } from 'utility/hooks';

interface FilterProps {
  keySuffix: string;
}

export const Filter = observer((props: FilterProps) => {
  const { sortFilterStore, searchCriteriaStore, routeStore, advertisingStore, context, analytics } = useStores();
  const { filterPresentation } = sortFilterStore;

  if (!filterPresentation) {
    return null;
  }

  const { keySuffix } = props;
  const { title, options, __typename } = filterPresentation;
  const filterOptions = options && (options as FlightsFilterOptionType[]);
  const { emptyFiltersMessage } =
    (__typename === 'FlightsFilterLoadedPresentation' && (filterPresentation as LoadedFilterPresentationType)) || {};
  const disabled = __typename === 'FlightsFilterLoadingPresentation';
  const legendClasses = classNames('uitk-type-heading-500 all-b-margin-six', {
    'is-disabled': disabled
  });

  const updateFilter = (keySuffix: string) => {
    const executeSearch = keySuffix === 'onACol';

    if (executeSearch) {
      sortFilterStore.applySortAndFilter(searchCriteriaStore, routeStore, advertisingStore, context, analytics);
    }
  };

  const renderFilterGroup = (filterOption: FlightsFilterOptionType, keySuffix: string, index: number) => {
    switch (filterOption.__typename) {
      case 'FlightsListDisplayFilterOption':
        return (
          <ListDisplayFilterGroup
            filterOption={filterOption}
            key={`filter-group-${filterOption.label}-${keySuffix}`}
            updateFilter={updateFilter}
            keySuffix={keySuffix}
          />
        );
      case 'FlightsCardsDisplayFilterOption':
        return (
          <CardDisplayFilterGroup
            filterOption={filterOption}
            key={`filter-group-${filterOption.label}-${keySuffix}`}
            updateFilter={updateFilter}
            keySuffix={keySuffix}
          />
        );
      case 'FlightsFilterSkeletonOption':
        return (
          <SkeletonFilterGroup
            disabled={disabled}
            filterOption={filterOption}
            key={`filter-group-skeleton-${keySuffix}-${index}`}
          />
        );
    }
  };

  return (
    <section className="all-b-margin-twelve" data-test-id="filter-container">
      <fieldset>
        <legend className={legendClasses} aria-hidden={true}>
          {title}
        </legend>
        <h2 className="is-visually-hidden">{title}</h2>
        {filterOptions && filterOptions.map((filterOption, index) => renderFilterGroup(filterOption, keySuffix, index))}
        {emptyFiltersMessage && (
          <UitkText align="left" size={200}>
            {emptyFiltersMessage}
          </UitkText>
        )}
      </fieldset>
    </section>
  );
});
